import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { api, errMsg, lim } from "../lib/api";
import { PageHeader, useLoad, Meter, StatusBadge } from "../components/common";
import LimitEditor from "../components/LimitEditor";
import TeamMembers from "../components/TeamMembers";

function TeamCard({ t, users, reload }) {
  const [name, setName] = useState(t.name);
  const [leader, setLeader] = useState(t.leader_user_id || "");
  const patch = async (body) => {
    try { await api.patch(`/teams/${t.id}`, body); toast.success("Team updated"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setLimit = async (b) => {
    try { await api.patch("/limits/teams", { ids: [t.id], ...b }); toast.success("Team limit saved"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-5 space-y-4 fade-in" data-testid={`team-card-${t.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input className="fld font-semibold flex-1 min-w-[180px]" value={name} onChange={(e) => setName(e.target.value)} data-testid={`team-name-${t.id}`} />
        <Button size="sm" variant="outline" onClick={() => patch({ name })} data-testid={`team-save-name-${t.id}`}>Rename</Button>
        <Button size="sm" variant={t.paused ? "default" : "outline"} onClick={() => patch({ paused: !t.paused, reason: "Team pause toggled" })} data-testid={`team-pause-${t.id}`}>{t.paused ? "Resume team" : "Pause team"}</Button>
        {t.paused && <StatusBadge s="rejected" label="paused" />}
      </div>
      <div className="text-[11px] mono text-zinc-500">id {t.id}{t.source_label ? ` · source "${t.source_label}"` : ""}</div>
      <div className="flex flex-wrap items-end gap-2 text-xs">
        <label className="space-y-1"><div>Team leader</div>
          <select className="fld w-56" value={leader} onChange={(e) => setLeader(e.target.value)} data-testid={`team-leader-select-${t.id}`}>
            <option value="">—</option>{users.filter((u) => u.role !== "none").map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}</select></label>
        <Button size="sm" onClick={() => patch({ leader_user_id: leader, reason: "Leader change" })} data-testid={`team-leader-save-${t.id}`}>Set leader</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Meter label={`Leads · ${t.capacity.mode === "manual" ? `shared ceiling ${lim(t.capacity.manual_ceiling)}` : "auto total"} (members sum ${t.capacity.member_sum})`} allowance={t.capacity.allowance} used={t.capacity.used} testid={`team-lead-meter-${t.id}`} />
        <Meter label={`Texts · ${t.text.mode} (1 per lead)`} allowance={t.text.allowance} used={t.text.texts_used} testid={`team-text-meter-${t.id}`} />
      </div>
      {t.capacity.mode === "manual" && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid={`team-manual-note-${t.id}`}>
          This team uses a fixed ceiling of {lim(t.capacity.manual_ceiling)}, so member limits don't change its total (members add up to {t.capacity.member_sum}).
          <Button size="sm" variant="outline" onClick={() => setLimit({ key: "lead_daily", value: null, effective: "now", mode: "auto", reason: "Use members' total" })} data-testid={`team-use-auto-${t.id}`}>Use members' total</Button>
        </div>)}
      <LimitEditor key={`${t.capacity.mode}-${t.capacity.member_sum}`} withMode onApply={setLimit} testid={`team-limit-${t.id}`} defaults={{ mode: t.capacity.mode }} />
      <TeamMembers team={t} users={users} reload={reload} />
      {t.subteams.length > 0 && <div className="text-xs">{t.subteams.map((s) => <div key={s.id}>Subteam <b>{s.name}</b> — {s.member_ids.length} members</div>)}</div>}
    </div>
  );
}

export default function TeamsPage() {
  const [teams, reloadT] = useLoad(async () => (await api.get("/teams")).data);
  const [users, reloadU] = useLoad(async () => (await api.get("/users")).data);
  const reload = () => { reloadT(); reloadU(); };
  const [newName, setNewName] = useState("");
  const create = async () => {
    try { await api.post("/teams", { name: newName }); setNewName(""); toast.success("Team created"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="teams-page">
      <PageHeader title="Teams, leaders & subteams" sub="Add or remove reps on each team card. Every team has a unique internal id.">
        <input className="fld" placeholder="New team name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-team-name" />
        <Button onClick={create} disabled={!newName} data-testid="create-team-btn">Create team</Button>
      </PageHeader>
      <div className="grid xl:grid-cols-2 gap-4">{(teams || []).map((t) => <TeamCard key={t.id} t={t} users={users || []} reload={reload} />)}</div>
    </div>
  );
}
